import { CommonModule } from '@angular/common';
import { Component, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BsModalRef, BsModalService, ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { SrcPath } from 'src/app/constants/srcPath';
import { CommonService } from 'src/app/services/common-service/common.service';
import { ColumnConfig, GridConfig, PrimengGridComponent } from 'src/app/shared/primeng-grid/primeng-grid.component';
import { ButtonRendererComponent } from '../../renderer/button-renderer.component';
import { PrimengTemplateDirective } from 'src/app/directives/primeng-template.directive';
import { Labels } from 'src/app/constants/labels';

interface AiConfig  {
  id: number;
  model: string;
  apiKey: string;
  endpointUrl: string;
}

interface PromptConfig {
  id: number;
  prompt_id: number;
  purpose: string;
  enabled: boolean;
  promptText: string;
}

@Component({
  selector: 'app-customer-ai-settings-popup',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ModalModule, ButtonRendererComponent, PrimengTemplateDirective, PrimengGridComponent],
  templateUrl: './customer-ai-settings-popup.component.html',
  styleUrl: './customer-ai-settings-popup.component.scss'
})
export class CustomerAiSettingsPopupComponent {
  aiConfig = signal<AiConfig>({} as AiConfig);

  prompts = signal<PromptConfig[]>([]);

  activeEditItem = signal<PromptConfig>({} as PromptConfig);
  cusotmerId: number = 0;
  srcPath = SrcPath;
  @ViewChild('editPromptModal', { static: true }) editPromptModal!: ModalDirective;
  columnDefPrimeNG: ColumnConfig[] = [];
  gridConfigPrimeNG: GridConfig = {};
  AiLabels = Labels.customerAiSettings;

  constructor(private fb: FormBuilder, private modalRefpopup: BsModalRef, public modalService: BsModalService, private commonService: CommonService) {
    this.columnDefPrimeNG = [
      { header: 'SI', field: 'id', width: '5' },
      { header: 'Purpose', field: 'purpose', width: '20', sortable: true, filter: true, filterType: 'text' },
      { header: 'Status', field: 'enabled', width: '10', template: 'statusTemplate' },
      { header: 'Prompt', field: 'promptText', sortable: true, filter: true, filterType: 'text' },
      { header: 'Edit', field: 'edit', width: '6', template: 'buttonRenderer' },
      { header: 'Delete', field: 'delete', width: '6', template: 'buttonRenderer' }
    ]

    this.gridConfigPrimeNG = {
      paginator: true,
      showEmptyMessage: true,
      rows: 7,
      hasCustomRowHeight: true,
      customRowHeight: 40
    };
  }

  ngOnInit() {
    this.loadAiSettings();
    this.loadAiPromptDetails();
  }

  loadAiSettings() {
    this.commonService.getAiSettings(this.cusotmerId).subscribe({
      next: (res: any) => {
        if (res && res['table'] && res.table.length > 0) {
          this.aiConfig.set({
            id: res.table[0].gstibo_ai_settings_id,
            model: res.table[0].gstibo_ai_model,
            apiKey: res.table[0].gstibo_api_key,
            endpointUrl: res.table[0].gstibo_base_endpoint_url
          })
        } else 
          this.aiConfig.set({} as AiConfig);
      },
      error: (err) => {
        console.error('Failed to load Ai settings!.', err);
        this.aiConfig.set({} as AiConfig);
      }
    })
  }

  loadAiPromptDetails() {
    this.commonService.getAllAiPrompts(this.cusotmerId).subscribe({
      next: (res: any) => {
        let promptData: PromptConfig[] = [];
        if (res && res['table'] && res.table.length > 0) {
          res.table.forEach((item: any, index: number) => {
            promptData.push({
              id: index + 1,
              prompt_id: item.gstibo_prompt_id,
              purpose: item.gstibo_purpose,
              enabled: item.gstibo_is_enabled === "Y" ? true : false,
              promptText: item.gstibo_system_prompt
            })
          });
        } 
        this.prompts.set(promptData);
      },
      error: (err) => {
        console.error('Failed to load Ai settings!.', err);
        this.prompts.set([]);
      }
    })
  }

  saveAiConfig() {
    const config = this.aiConfig();
    if (config && config.model && config.apiKey && config.endpointUrl) {
      this.commonService.addUpdateAiSettings(config, this.cusotmerId).subscribe({
        next: (res: any) => {
          if (res && res.table[0].success)
            this.commonService.showModalValidate(this.AiLabels.messages.save_settings, '', this.AiLabels.messages.info, this.AiLabels.messages.save_settings);
          else 
            this.commonService.showModalValidate(this.AiLabels.messages.save_settings_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.save_settings_failed);
        },
        error: (err) => {
          console.error('Error while updating Ai configuration details!.', err);
          this.commonService.showModalValidate(this.AiLabels.messages.save_settings_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.save_settings_failed);
        }
      })
    } else {
      this.commonService.showModalValidate(this.AiLabels.messages.missing_fields, '', this.AiLabels.messages.warning, this.AiLabels.messages.missing_fields);
    }
  }

  toggleStatus(row: PromptConfig) {
    this.prompts.update(list =>
      list.map(p => p.id === row.id ? { ...p, enabled: !p.enabled } : p)
    );
    const nextStatus = !row.enabled;
    this.commonService.customerAiPromptStatusUpdate(row.prompt_id, 'status', true, nextStatus).subscribe({
      next: (res: any) => {
        if (res && res.table[0].success) 
          this.commonService.showModalValidate(this.AiLabels.messages.status_success, '', this.AiLabels.messages.info, this.AiLabels.messages.status_success);
        else
          this.commonService.showModalValidate(this.AiLabels.messages.status_error, '', this.AiLabels.messages.error, this.AiLabels.messages.status_error);
      },
      error: (err) => {
        console.error('Error while updating status!.', err);
        this.commonService.showModalValidate(this.AiLabels.messages.status_error, '', this.AiLabels.messages.error, this.AiLabels.messages.status_error);
        this.prompts.update(list =>
          list.map(p => p.id === row.id ? { ...p, enabled: !row.enabled } : p)
        );
      }
    })
  }

  deleteItem(item: PromptConfig) {
    this.commonService.customerAiPromptStatusUpdate(item.prompt_id, 'delete', true).subscribe({
      next: (res: any) => {
        if (res && res.table[0].success) {
          this.loadAiPromptDetails();
          this.commonService.showModalValidate(this.AiLabels.messages.delete_prompt, '', this.AiLabels.messages.info, this.AiLabels.messages.delete_prompt);
        }
        else
          this.commonService.showModalValidate(this.AiLabels.messages.delete_prompt_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.delete_prompt_failed);
      },
      error: (err) => {
        console.error('Error while deleting prompt!.', err);
        this.commonService.showModalValidate(this.AiLabels.messages.delete_prompt_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.delete_prompt_failed);
      }
    })
  }

  closepopup() {
    this.modalRefpopup.hide();
  }

  openEditModal(item?: PromptConfig) {
    if (item) 
      this.activeEditItem.set({ ...item });
    this.editPromptModal.show();
  }

  savePromptEdit() {
    const updatedItem = this.activeEditItem();
    if (!updatedItem || !updatedItem.purpose || !updatedItem.promptText) 
      this.commonService.showModalValidate(this.AiLabels.messages.missing_fields, '', this.AiLabels.messages.warning, this.AiLabels.messages.missing_fields);
    else {
      this.commonService.addUpdateAiPrompts(updatedItem, this.cusotmerId).subscribe({
        next: (res: any) => {
          if (res?.table[0]?.success === 1) {
            this.commonService.showModalValidate(this.AiLabels.messages.save_prompt, '', this.AiLabels.messages.info, this.AiLabels.messages.save_prompt);
            this.loadAiPromptDetails();
            this.activeEditItem.set({} as PromptConfig);

            this.editPromptModal.hide();
          } else
            this.commonService.showModalValidate(this.AiLabels.messages.save_prompt_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.save_prompt_failed);
        },
        error: (err) => {
          console.error('Error while updating Ai Prompt details!.', err);
          this.commonService.showModalValidate(this.AiLabels.messages.save_prompt_failed, '', this.AiLabels.messages.error, this.AiLabels.messages.save_prompt_failed);
        }
      })
    }
  }

  closeEditModal() {
    this.activeEditItem.set({} as PromptConfig);
    this.editPromptModal.hide();
  }

  buildParams(row: any, col: any, rowIndex: number) {
    return {
      label: this.getLabel(col),
      value: row[col.field],
      data: row,
      cellEdit: col.clickable ? '1' : '0',
      node: { id: rowIndex },
      column: { colDef: col },
      colDef: col,
      onClick: (payload: any) => { this.handleTableAction(col, { ...payload, rowData: row, rowcol: 'main' }); }
    };
  }

  getLabel(col: any): string {
    switch (col.field) {
      case 'edit': return 'edits';
      case 'delete': return 'trash';
      default: return col.field;
    }
  }

  handleTableAction(col: any, payload: any) {
    const row = payload.rowData;
    switch (col.field) {
      case 'edit': this.openEditModal(row); break;
      case 'delete': this.deleteItem(row); break;
      default: console.log('Unhandled action', col, row);
    }
  }

}
